import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { env } from '@/config/env';

export const Route = createFileRoute('/dashboard/feeds/')({
  component: EventPickerPage,
});

const BASE = env.API_BASE_URL + '/api/admin';

async function getJson<T>(path: string, params?: Record<string, any>): Promise<T> {
  const qs = params
    ? '?' +
      new URLSearchParams(
        Object.entries(params)
          .filter(([_, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)])
      ).toString()
    : '';

  const res = await fetch(`${BASE}${path}${qs}`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json() as Promise<T>;
}

type EventItem = {
  id: number;
  name?: string;
  sport?: string;
  state?: string;
  eventTime?: number;
  fullPath?: string;
};

function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function toDatetimeLocalValue(ms: number) {
  // datetime-local wants "YYYY-MM-DDTHH:mm"
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

function fromDatetimeLocalValue(v: string) {
  // treat as local time
  const d = new Date(v);
  return Number.isFinite(d.getTime()) ? d.getTime() : null;
}

function EventPickerPage() {
  const navigate = Route.useNavigate();
  const now = Date.now();

  // friendlier default window: yesterday -> +14 days
  const [fromMs, setFromMs] = React.useState(now - 24 * 60 * 60 * 1000);
  const [toMs, setToMs] = React.useState(now + 14 * 24 * 60 * 60 * 1000);

  // search
  const [q, setQ] = React.useState('');
  const [sport, setSport] = React.useState('');
  const qDebounced = useDebouncedValue(q, 250);
  const sportDebounced = useDebouncedValue(sport, 250);

  // selection
  const [selected, setSelected] = React.useState<Record<string, true>>({});
  const [primaryId, setPrimaryId] = React.useState<number | null>(null);

  const hasSearch = qDebounced.trim().length > 0 || sportDebounced.trim().length > 0;

  const eventsQ = useQuery({
    queryKey: ['feeds', 'events-search', { fromMs, toMs, qDebounced, sportDebounced }],
    queryFn: async () => {
      // always use search endpoint now; it can still “browse” when q/sport empty
      return getJson<{ events: EventItem[] }>('/events/search', {
        q: qDebounced.trim(),
        sport: sportDebounced.trim(),
        from: fromMs,
        to: toMs,
        limit: 200, // no pagination; pull a reasonable cap
        offset: 0,
      });
    },
    refetchOnWindowFocus: false,
    staleTime: 5_000,
    gcTime: 5 * 60 * 1000,
    placeholderData: prev => prev,
  });

  const items: EventItem[] = eventsQ.data?.events ?? [];

  const selectedIds = React.useMemo(
    () =>
      Object.keys(selected)
        .map(x => Number(x))
        .filter(n => Number.isFinite(n)),
    [selected]
  );

  // keep primaryId valid when selection changes
  React.useEffect(() => {
    if (primaryId != null && selected[String(primaryId)]) return;

    const next = Object.keys(selected)
      .map(Number)
      .find(n => Number.isFinite(n));

    setPrimaryId(next ?? null);
  }, [selected, primaryId]);

  const toggleSelected = (id: number) => {
    const k = String(id);
    setSelected(prev => {
      const next = { ...prev };
      if (next[k]) delete next[k];
      else next[k] = true;
      return next;
    });

    // if nothing primary yet, set it to the first thing selected (effect will also keep it sane)
    setPrimaryId(prev => (prev == null ? id : prev));
  };

  const clearSelection = () => {
    setSelected({});
    setPrimaryId(null);
  };

  const selectAllVisible = () => {
    setSelected(prev => {
      const next = { ...prev };
      for (const ev of items) next[String(ev.id)] = true;
      return next;
    });
    setPrimaryId(prev => {
      if (prev != null) return prev;
      return items.length > 0 ? items[0].id : null;
    });
  };

  const unselectAllVisible = () => {
    setSelected(prev => {
      const next = { ...prev };
      for (const ev of items) delete next[String(ev.id)];
      return next;
    });
  };

  const clearSearch = () => {
    setQ('');
    setSport('');
  };

  const onBuildMulti = () => {
    if (selectedIds.length === 0) return;

    const primaryEventId =
      primaryId != null && selected[String(primaryId)] ? primaryId : selectedIds[0];

    navigate({
      to: '/dashboard/feeds/$eventId/build',
      params: { eventId: String(primaryEventId) },
      search: { eventIds: selectedIds.join(',') },
    });
  };

  const setPrimary = (id: number) => {
    // ensure it’s selected
    setSelected(prev => ({ ...prev, [String(id)]: true }));
    setPrimaryId(id);
  };

  return (
    <div style={{ padding: 16, display: 'grid', gap: 12, maxWidth: 1200 }}>
      <h2>TDC Feed → Choose event(s)</h2>

      {/* Controls */}
      <div
        style={{
          display: 'grid',
          gap: 10,
          padding: 12,
          border: '1px solid #333',
          borderRadius: 10,
        }}
      >
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
          <label style={{ display: 'grid', gap: 6 }}>
            From
            <input
              type="datetime-local"
              value={toDatetimeLocalValue(fromMs)}
              onChange={e => {
                const v = fromDatetimeLocalValue(e.target.value);
                if (v != null) setFromMs(v);
              }}
              style={{ width: 220 }}
            />
          </label>

          <label style={{ display: 'grid', gap: 6 }}>
            To
            <input
              type="datetime-local"
              value={toDatetimeLocalValue(toMs)}
              onChange={e => {
                const v = fromDatetimeLocalValue(e.target.value);
                if (v != null) setToMs(v);
              }}
              style={{ width: 220 }}
            />
          </label>

          <label style={{ display: 'grid', gap: 6, marginLeft: 10 }}>
            Search
            <input
              placeholder="Team, league, city…"
              value={q}
              onChange={e => setQ(e.target.value)}
              style={{ width: 280 }}
            />
          </label>

          <label style={{ display: 'grid', gap: 6 }}>
            Sport
            <input
              placeholder="e.g. soccer"
              value={sport}
              onChange={e => setSport(e.target.value)}
              style={{ width: 160 }}
            />
          </label>

          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button onClick={() => eventsQ.refetch()} disabled={eventsQ.isFetching}>
              Refresh
            </button>
            <button onClick={clearSearch} disabled={!q && !sport}>
              Clear search
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ opacity: 0.8, fontSize: 12 }}>
            Showing <b>{items.length}</b> events
            {hasSearch ? ' (search)' : ' (browse)'} {eventsQ.isFetching ? ' · fetching…' : ''}
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ opacity: 0.8, fontSize: 12 }}>
              Selected: <b>{selectedIds.length}</b>
              {primaryId != null ? (
                <>
                  {' '}
                  · Primary: <b>#{primaryId}</b>
                </>
              ) : null}
            </div>
            <button onClick={selectAllVisible} disabled={items.length === 0}>
              Select all visible
            </button>
            <button onClick={unselectAllVisible} disabled={items.length === 0}>
              Unselect visible
            </button>
            <button onClick={clearSelection} disabled={selectedIds.length === 0}>
              Clear selection
            </button>
            <button onClick={onBuildMulti} disabled={selectedIds.length === 0}>
              Build multi-event pick’em →
            </button>
          </div>
        </div>
      </div>

      {eventsQ.isLoading && <div>Loading…</div>}
      {eventsQ.isError && (
        <div style={{ color: 'crimson' }}>Error: {(eventsQ.error as Error).message}</div>
      )}

      {/* Results */}
      <div style={{ border: '1px solid #333', borderRadius: 10, overflow: 'hidden' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '60px 2fr 1fr 1fr 1fr',
            padding: 10,
            borderBottom: '1px solid #333',
            fontWeight: 700,
          }}
        >
          <div>Select</div>
          <div>Event</div>
          <div>Sport</div>
          <div>Start</div>
          <div />
        </div>

        {items.map(ev => {
          const checked = Boolean(selected[String(ev.id)]);
          const isPrimary = primaryId === ev.id;

          return (
            <div
              key={ev.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '60px 2fr 1fr 1fr 1fr',
                padding: 10,
                borderTop: '1px solid #333',
                alignItems: 'center',
              }}
            >
              <div>
                <input type="checkbox" checked={checked} onChange={() => toggleSelected(ev.id)} />
              </div>

              <div>
                <div style={{ fontWeight: 600 }}>
                  {ev.name ?? '(unnamed)'} <span style={{ opacity: 0.6 }}>#{ev.id}</span>
                  {isPrimary ? (
                    <span style={{ marginLeft: 8, opacity: 0.8 }}>· Primary</span>
                  ) : null}
                </div>
                <div style={{ opacity: 0.7, fontSize: 12 }}>
                  {ev.state ?? '—'}
                  {ev.fullPath ? <span style={{ opacity: 0.8 }}> · {ev.fullPath}</span> : null}
                </div>
              </div>

              <div>{ev.sport ?? '—'}</div>
              <div>{ev.eventTime ? new Date(ev.eventTime).toLocaleString() : '—'}</div>

              <div style={{ justifySelf: 'end', display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setPrimary(ev.id)}
                  disabled={isPrimary}
                  title="Primary controls which eventId is used in the builder route param"
                >
                  {isPrimary ? 'Primary' : 'Set primary'}
                </button>

                <button
                  onClick={() =>
                    navigate({
                      to: '/dashboard/feeds/$eventId/build',
                      params: { eventId: String(ev.id) },
                    })
                  }
                >
                  Open builder →
                </button>
              </div>
            </div>
          );
        })}

        {items.length === 0 && !eventsQ.isLoading && (
          <div style={{ padding: 12, opacity: 0.75 }}>No events found for that query/window.</div>
        )}
      </div>
    </div>
  );
}
